/**
 * Helpers for safely associating BookieBurglar bet labels with the
 * Kalshi market ticker we'd send an order to.
 *
 * Why this exists: an arb often pairs `Over 27.5 @ DK` with
 * `Under 30.5 @ Kalshi`. The Odds API only attaches its `sid` (which is
 * the Kalshi ticker) on the Kalshi-bookied outcome. If the UI naively
 * reuses "any kalshi ticker on the row" for both sides, clicking OVER
 * routes to the 30.5 ticker — wrong threshold, often empty book on the
 * YES side, IOC never fills. That's the headline "OVER doesn't work"
 * symptom.
 *
 * The two safest invariants:
 *   1. Each {@code GroupedLeg} carries its own ticker, only when the
 *      kalshi leg's name (and threshold) matches the group's name.
 *   2. Before rendering the inline CTA, parse the threshold out of
 *      the ticker and the leg label — if they disagree, suppress the
 *      CTA on that side.
 */

const TICKER_THRESHOLD_RE = /-T(\d+(?:\.\d+)?)(?:[^\d]|$)/i;
const LABEL_THRESHOLD_RE = /(?:over|under)\s+(\d+(?:\.\d+)?)/i;

/**
 * Parse the threshold (e.g. {@code 29.5}) out of a Kalshi market ticker
 * like {@code KXNBAPTS-25APR15LAL-T29.5}. Returns {@code null} when the
 * ticker doesn't follow the {@code -T<number>} convention (some markets
 * are non-totals binary contracts, e.g. moneyline-style yes/no).
 */
export function parseKalshiTickerThreshold(
  ticker: string | null | undefined
): number | null {
  if (!ticker) return null;
  const m = ticker.match(TICKER_THRESHOLD_RE);
  if (!m) return null;
  const v = Number(m[1]);
  return Number.isFinite(v) ? v : null;
}

/**
 * Parse the threshold out of a leg label like {@code Over 29.5} or
 * {@code Nikola Jokic Under 27.5 Points}. Returns {@code null} for
 * non-totals labels (moneyline, spread without "over"/"under" keyword).
 */
export function parseLabelThreshold(
  label: string | null | undefined
): number | null {
  if (!label) return null;
  const m = label.match(LABEL_THRESHOLD_RE);
  if (!m) return null;
  const v = Number(m[1]);
  return Number.isFinite(v) ? v : null;
}

/**
 * Returns true when the ticker is safe to use as the trade target for
 * the given leg label. We're permissive on ambiguity: when either side
 * lacks a parseable threshold (non-totals market, or a server-supplied
 * threshold field on the leg), we assume it's a binary YES/NO contract
 * where the same ticker covers both sides and let the caller through.
 *
 * The only case we block is the diagnostic one: ticker says T30.5 but
 * the leg label says "Over 27.5" — that's the misroute we're trying
 * to prevent.
 */
export function tickerMatchesLabel(
  ticker: string | null | undefined,
  label: string | null | undefined,
  /** Optional threshold from the backend (preferred when present). */
  serverTickerThreshold?: number | null
): boolean {
  if (!ticker) return false;
  const tickerThreshold =
    serverTickerThreshold ?? parseKalshiTickerThreshold(ticker);
  const labelThreshold = parseLabelThreshold(label);
  if (tickerThreshold == null || labelThreshold == null) return true;
  return Math.abs(tickerThreshold - labelThreshold) < 1e-9;
}
